import { smoothScrollTo } from './_scrolling.js';

/* --------------------------------------------------------------------------
 * accordions-carousel.js
 *
 * Lightweight, dependency-free helper that turns any markup matching the
 * `.accordions-carousel` pattern (see README) into an accessible accordion
 * where clicking a `.accordions-carousel__panel` moves the `.active` class to
 * the correct `.accordions-carousel__item`, and clicking the active panel
 * again will collapse (close) it.
 *
 * Uses CSS transforms on the track to position the active item within the
 * visible bounds, clamped to the start/end so no DOM shift occurs.
 * ------------------------------------------------------------------------ */

class AccordionsCarousel {
  /**
   * @param {HTMLElement} root – the element with class `.accordions-carousel`.
   */
  constructor(root) {
    this.root = root;
    this.items = Array.from(
      root.querySelectorAll('.accordions-carousel__item')
    );
    this.panels = this.items.map((item) =>
      item.querySelector('.accordions-carousel__panel')
    );
    this.track = root.querySelector('.accordions-carousel__track');

    // Prepare track for CSS transforms
    if (this.track) {
      this.track.style.willChange = 'transform';
      this.track.style.transition = 'transform 0.3s ease';
    }

    this._bindEvents();
  }

  /* ---------------------------------------------------------------------- */
  /* private helpers                                                         */
  /* ---------------------------------------------------------------------- */

  _bindEvents() {
    this.panels.forEach((panel) => {
      panel.addEventListener('click', () => {
        const item = panel.closest('.accordions-carousel__item');
        this._activateItem(item);
      });
      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const item = panel.closest('.accordions-carousel__item');
          this._activateItem(item);
        }
      });
    });
  }

  /**
   * Toggles the clicked item: if not active, activates and recenters it;
   * if already active, collapses all items and resets track position.
   * @param {HTMLElement} item
   */
  _activateItem(item) {
    if (!item) return;

    const wasActive = item.classList.contains('active');

    // Collapse all items
    this.items.forEach((it) => {
      const title = it.querySelector('.accordions-carousel__title');
      const answer = it.querySelector('.accordions-carousel__answer');
      it.classList.remove('active');
      if (title) title.setAttribute('aria-expanded', 'false');
      if (answer) answer.setAttribute('aria-hidden', 'true');
    });

    if (wasActive) {
      // Reset track to start (optional: you can clamp or keep position)
      this._translateTrack(0);
      return;
    }

    // Otherwise, activate the clicked item
    item.classList.add('active');
    const title = item.querySelector('.accordions-carousel__title');
    const answer = item.querySelector('.accordions-carousel__answer');
    if (title) title.setAttribute('aria-expanded', 'true');
    if (answer) answer.setAttribute('aria-hidden', 'false');

    this._translateTrackToItem(item);
  }

  /**
   * Translates track to a specific offset (px).
   * @param {number} offset
   */
  _translateTrack(offset) {
    if (this.track) {
      this.track.style.transform = `translateX(-${offset}px)`;
    }
  }

  /**
   * Calculates and applies translateX to bring item into view,
   * centering if possible, else clamping at edges.
   * @param {HTMLElement} item
   */
  _translateTrackToItem(item) {
    if (!this.track) return;

    const container = this.track.parentElement;
    const containerWidth = container.clientWidth;
    const trackWidth = this.track.scrollWidth;
    const itemOffsetLeft = item.offsetLeft;
    const itemWidth = item.clientWidth;

    // Center the item
    let targetOffset = itemOffsetLeft - (containerWidth - itemWidth) / 2;
    // Clamp between 0 and max
    const maxOffset = trackWidth - containerWidth;
    targetOffset = Math.max(0, Math.min(targetOffset, maxOffset));

    this._translateTrack(targetOffset);
    this._scrollItemIntoView(item);
  }

  /**
   * Scroll the chosen item into view, leaving room for a fixed nav bar
   * and optionally centring it in the viewport.
   * @param {HTMLElement} item
   */
  _scrollItemIntoView (item) {
    // delay until the CSS transition that expands the panel has finished
    setTimeout(() => {
      const nav        = document.querySelector('.navigation');
      const navHeight  = nav ? nav.offsetHeight : 0;

      const rect            = item.getBoundingClientRect();
      const itemTopAbs      = rect.top + window.scrollY;
      const viewportHeight  = window.innerHeight;

      /* If the top of the panel is already below the fixed nav
         **and** the whole panel fits, do nothing            */
      const fullyVisible =
        rect.top   >= navHeight &&
        rect.bottom <= viewportHeight;

      if (fullyVisible) return;

      /* Centre the panel when possible, otherwise clamp so that the
         first pixel of the panel sits just below the nav bar          */
      const target =
        rect.height > viewportHeight
          ? itemTopAbs - navHeight                    // very tall panel
          : itemTopAbs - (viewportHeight - rect.height) / 2; // centre

      /* Use native smooth-scroll where available; otherwise fall back */
      const smoothOK =
        'scrollBehavior' in document.documentElement.style &&
        !/Safari/.test(navigator.userAgent);

      (smoothOK)
        ? window.scrollTo({ top: target, behavior: 'smooth' })
        : smoothScrollTo(target, 500);
    }, 300);        // “300” ≈ the CSS transition-duration of your accordion
  }

  /* ---------------------------------------------------------------------- */
  /* static helpers                                                          */
  /* ---------------------------------------------------------------------- */

  /**
   * Instantiates AccordionsCarousel for each matching element.
   */
  static initAll() {
    document.querySelectorAll('.accordions-carousel').forEach((el) => {
      if (!el.__accordionsCarouselInstance) {
        el.__accordionsCarouselInstance = new AccordionsCarousel(el);
      }
    });
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => AccordionsCarousel.initAll());
} else {
  AccordionsCarousel.initAll();
}